package org.teddapp.project.ui.finance

import org.teddapp.project.ui.LocalTeddFonts

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Finance_category
import com.tedd.db.Finance_transaction
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.ui.TeddIcon
import androidx.compose.foundation.layout.PaddingValues
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import androidx.compose.runtime.collectAsState
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.ui.TeddTokens

internal enum class DashGranularity(val label: String) { Day("Jour"), Week("Semaine"), Month("Mois") }

private data class DashBucket(val label: String, val start: String, val end: String, var expense: Long = 0L, var income: Long = 0L)

/**
 * Tableau de bord Finance : série temporelle des dépenses/entrées par jour, semaine
 * ou mois (barres Canvas maison), totaux de la période et top catégories de dépense.
 * Scopé au compte sélectionné.
 */
@Composable
internal fun DashboardTab(
    txns: List<Finance_transaction>,
    categories: List<Finance_category>,
    currentAccountId: String?,
    currency: String,
    compact: Boolean = false,
    onManageCategories: () -> Unit = {},
) {
    // Le seul grand nombre de l'écran : ce qui s'améliore par petites marches (v2).
    val monthVerdicts = remember(txns) {
        txns.filter { it.is_deleted == 0L && it.date in MONTH_START..MONTH_END }.map { it.verdict }
    }
    // Le noyau fait autorité sur le taux : c'est lui qui tient le registre des décisions.
    val coreFinance = LocalCoreSession.current?.snapshot?.collectAsState()?.value?.finance
    val localRate = remember(monthVerdicts) { rationalRate(monthVerdicts) }
    val rate = coreFinance?.taux ?: localRate
    val fromCore = coreFinance?.taux != null
    val needCount = monthVerdicts.count { it == Verdict.NEED }
    val masteredCount = monthVerdicts.count { it == Verdict.MASTERED }
    val impulseCount = monthVerdicts.count { it == Verdict.IMPULSE }
    var granularity by remember { mutableStateOf(DashGranularity.Day) }
    val hPad = if (compact) 16.dp else 28.dp
    val hidden = LocalHideBalance.current

    val buckets = remember(txns, granularity, currentAccountId) {
        buildDashBuckets(txns, currentAccountId, granularity, TODAY_ISO)
    }
    val rangeStart = buckets.firstOrNull()?.start ?: TODAY_ISO
    val inRange = remember(txns, rangeStart, currentAccountId) {
        txns.filter {
            it.is_deleted == 0L && it.date >= rangeStart && it.date <= TODAY_ISO &&
                (it.account_id == currentAccountId || it.to_account_id == currentAccountId)
        }
    }
    val totalExpense = buckets.sumOf { it.expense }
    val totalIncome = buckets.sumOf { it.income }
    val net = totalIncome - totalExpense

    val topCats = remember(inRange, categories) {
        inRange.filter { it.type == "expense" && it.cat_id != null }
            .groupBy { it.cat_id!! }
            .mapValues { e -> e.value.sumOf { it.amount } }
            .entries.sortedByDescending { it.value }.take(6)
            .mapNotNull { e -> categories.firstOrNull { it.id == e.key }?.let { it to e.value } }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(start = hPad, end = hPad, top = 12.dp, bottom = 40.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        // Sélecteur de granularité
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            DashGranularity.entries.forEach { g ->
                val on = g == granularity
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .background(if (on) TeddTokens.LimeSoft else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))
                        .border(1.dp, if (on) TeddTokens.LimeStrong else TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { granularity = g },
                        )
                        .padding(horizontal = 14.dp, vertical = 7.dp),
                ) {
                    Text(
                        g.label,
                        color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                        fontSize = 12.5.sp,
                        fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
        }

        // Taux de décisions rationnelles du mois : d'abord, avant les montants.
        RationalRateCard(
            rate = rate,
            needCount = needCount,
            masteredCount = masteredCount,
            impulseCount = impulseCount,
            fromCore = fromCore,
        )

        // Totaux de la période (montants masqués si le toggle « cacher » est actif)
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            DashStat("Dépenses", totalExpense, TeddTokens.Danger, currency, hidden, Modifier.weight(1f))
            DashStat("Entrées", totalIncome, TeddTokens.Success, currency, hidden, Modifier.weight(1f))
            DashStat("Net", net, if (net >= 0) TeddTokens.Success else TeddTokens.Danger, currency, hidden, Modifier.weight(1f))
        }

        val maxVal = buckets.maxOf { maxOf(it.expense, it.income) }.coerceAtLeast(1L)
        // Deux cadres distincts : un graphe à barres, un graphe à lignes.
        DashChartCard(title = "Barres", bars = true, buckets = buckets, maxVal = maxVal, currency = currency, hidden = hidden)
        DashChartCard(title = "Courbe", bars = false, buckets = buckets, maxVal = maxVal, currency = currency, hidden = hidden)

        // Top catégories de dépense
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                "OÙ PART L'ARGENT",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.6.sp,
                modifier = Modifier.weight(1f),
            )
            TeddButton(
                text = "Gérer les catégories",
                onClick = onManageCategories,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = { TeddIcon(name = "category", size = 12.dp, color = TeddTokens.InkMuted) },
            )
        }
        if (topCats.isNotEmpty()) {
            val maxCat = topCats.maxOf { it.second }.coerceAtLeast(1L)
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                topCats.forEach { (cat, amount) ->
                    val c = runCatching { parseFinHex(cat.color) }.getOrElse { TeddTokens.LimeStrong }
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Box(
                                modifier = Modifier.size(22.dp).clip(RoundedCornerShape(TeddTokens.RXs))
                                    .background(c.copy(alpha = 0.14f), RoundedCornerShape(TeddTokens.RXs)),
                                contentAlignment = Alignment.Center,
                            ) { TeddIcon(name = cat.icon, size = 12.dp, color = c, strokeWidth = 2.dp) }
                            Text(cat.name, color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.Medium, modifier = Modifier.weight(1f))
                            Text(fmtMoneyMasked(amount, currency, hidden, compact = true), color = TeddTokens.Ink, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold, fontFamily = LocalTeddFonts.current.mono)
                        }
                        Box(
                            modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull)),
                        ) {
                            Box(
                                modifier = Modifier.fillMaxWidth(amount.toFloat() / maxCat).height(6.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RFull)).background(c, RoundedCornerShape(TeddTokens.RFull)),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DashChartCard(
    title: String,
    bars: Boolean,
    buckets: List<DashBucket>,
    maxVal: Long,
    currency: String,
    hidden: Boolean,
) {
    val expColor = TeddTokens.Danger
    val incColor = TeddTokens.Success
    val gridColor = TeddTokens.Line.copy(alpha = 0.5f)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(title.uppercase(), color = TeddTokens.InkMuted, fontSize = 10.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.5.sp)
            LegendDot(expColor, "Dépenses")
            LegendDot(incColor, "Entrées")
        }
        if (buckets.all { it.expense == 0L && it.income == 0L }) {
            Box(modifier = Modifier.fillMaxWidth().height(140.dp), contentAlignment = Alignment.Center) {
                Text("Aucun mouvement sur la période.", color = TeddTokens.InkMuted, fontSize = 12.sp)
            }
        } else {
            Row(modifier = Modifier.fillMaxWidth().height(150.dp)) {
                Column(
                    modifier = Modifier.fillMaxHeight().width(46.dp).padding(end = 4.dp),
                    verticalArrangement = Arrangement.SpaceBetween,
                    horizontalAlignment = Alignment.End,
                ) {
                    Text(fmtMoneyMasked(maxVal, currency, hidden, compact = true), color = TeddTokens.InkMuted, fontSize = 8.5.sp, maxLines = 1)
                    Text(fmtMoneyMasked(maxVal / 2, currency, hidden, compact = true), color = TeddTokens.InkMuted, fontSize = 8.5.sp, maxLines = 1)
                    Text("0", color = TeddTokens.InkMuted, fontSize = 8.5.sp)
                }
                Canvas(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight(),
                ) {
                    val n = buckets.size
                    if (n == 0) return@Canvas
                    val slot = size.width / n
                    val h = size.height
                    fun cx(i: Int) = i * slot + slot / 2f
                    fun y(v: Long) = h - (v.toFloat() / maxVal) * h
                    listOf(0f, h / 2f, h - 0.5f).forEach { gy ->
                        drawLine(gridColor, Offset(0f, gy), Offset(size.width, gy), strokeWidth = 0.5.dp.toPx())
                    }
                    if (bars) {
                        val barW = slot * 0.30f
                        val gap = slot * 0.06f
                        buckets.forEachIndexed { i, b ->
                            val c = cx(i)
                            drawRect(incColor, topLeft = Offset(c - barW - gap / 2f, y(b.income)), size = Size(barW, h - y(b.income)))
                            drawRect(expColor, topLeft = Offset(c + gap / 2f, y(b.expense)), size = Size(barW, h - y(b.expense)))
                        }
                    } else {
                        for (i in 0 until n - 1) {
                            drawLine(incColor, Offset(cx(i), y(buckets[i].income)), Offset(cx(i + 1), y(buckets[i + 1].income)), strokeWidth = 1.8.dp.toPx(), cap = StrokeCap.Round)
                            drawLine(expColor, Offset(cx(i), y(buckets[i].expense)), Offset(cx(i + 1), y(buckets[i + 1].expense)), strokeWidth = 1.8.dp.toPx(), cap = StrokeCap.Round)
                        }
                        buckets.forEachIndexed { i, b ->
                            drawCircle(incColor, radius = 2.dp.toPx(), center = Offset(cx(i), y(b.income)))
                            drawCircle(expColor, radius = 2.dp.toPx(), center = Offset(cx(i), y(b.expense)))
                        }
                    }
                }
            }
            Row(modifier = Modifier.fillMaxWidth().padding(start = 46.dp)) {
                buckets.forEach { b ->
                    Text(
                        text = b.label,
                        color = TeddTokens.InkMuted,
                        fontSize = 8.5.sp,
                        maxLines = 1,
                        modifier = Modifier.weight(1f),
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                    )
                }
            }
        }
    }
}

@Composable
private fun DashStat(label: String, value: Long, color: Color, currency: String, hidden: Boolean, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(3.dp),
    ) {
        Text(label.uppercase(), color = TeddTokens.InkMuted, fontSize = 9.5.sp, fontWeight = FontWeight.SemiBold, letterSpacing = 0.4.sp)
        Text(
            fmtMoneyMasked(value, currency, hidden, compact = true),
            color = color,
            fontSize = 15.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = LocalTeddFonts.current.mono,
            maxLines = 1,
        )
    }
}

@Composable
private fun LegendDot(color: Color, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
        Box(modifier = Modifier.size(8.dp).clip(RoundedCornerShape(2.dp)).background(color))
        Text(label, color = TeddTokens.InkMuted, fontSize = 10.5.sp)
    }
}

/** Lundi-index (0=lundi) de la date ISO (1970-01-01 = jeudi). */
private fun mondayIndex(iso: String): Int {
    val dn = isoToDayNumber(iso) ?: return 0
    return (((dn % 7L) + 3L) % 7L + 7L).toInt() % 7
}

private fun buildDashBuckets(
    txns: List<Finance_transaction>,
    accountId: String?,
    g: DashGranularity,
    today: String,
): List<DashBucket> {
    val buckets: List<DashBucket> = when (g) {
        DashGranularity.Day -> (0..13).map { i ->
            val d = isoMinusDays(today, 13 - i)
            DashBucket(label = d.substring(8, 10), start = d, end = d)
        }
        DashGranularity.Week -> {
            val thisMonday = isoMinusDays(today, mondayIndex(today))
            (0..11).map { i ->
                val ws = isoMinusDays(thisMonday, (11 - i) * 7)
                val we = org.teddapp.project.ui.pomodoro.isoAddDays(ws, 6)
                DashBucket(label = ws.substring(8, 10) + "/" + ws.substring(5, 7), start = ws, end = we)
            }
        }
        DashGranularity.Month -> {
            val y = today.substring(0, 4).toInt()
            val m = today.substring(5, 7).toInt()
            (0..11).map { i ->
                val back = 11 - i
                var yy = y
                var mm = m - back
                while (mm <= 0) { mm += 12; yy -= 1 }
                val mmStr = mm.toString().padStart(2, '0')
                DashBucket(
                    label = "$mmStr/" + yy.toString().substring(2),
                    start = "$yy-$mmStr-01",
                    end = "$yy-$mmStr-31",
                )
            }
        }
    }
    for (tx in txns) {
        if (tx.is_deleted != 0L) continue
        if (tx.account_id != accountId && tx.to_account_id != accountId) continue
        val b = buckets.firstOrNull { tx.date >= it.start && tx.date <= it.end } ?: continue
        when (tx.type) {
            "expense" -> b.expense += tx.amount
            "income" -> b.income += tx.amount
            // transferts : ignorés dans la lecture dépenses/revenus
        }
    }
    return buckets
}

/**
 * Taux de décisions rationnelles du mois : le seul grand nombre du tableau. Les montants sont
 * secondaires ; c'est ce chiffre qui s'améliore par petites marches.
 */
@Composable
private fun RationalRateCard(rate: Int?, needCount: Int, masteredCount: Int, impulseCount: Int, fromCore: Boolean = false) {
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(18.dp)) {
        Text(
            text = if (fromCore) "DÉCISIONS RATIONNELLES · CALCULÉ PAR LE NOYAU" else "DÉCISIONS RATIONNELLES · CE MOIS",
            style = TeddType.Overline,
            color = TeddTokens.InkMuted,
        )
        if (rate == null) {
            Text(
                text = "Aucune dépense majeure ce mois-ci.",
                style = TeddType.BodyLg,
                color = TeddTokens.InkMuted,
                modifier = Modifier.padding(top = 8.dp),
            )
            Text(
                text = "Le filtre s'affiche au-delà d'un quart du solde, et le taux apparaît ici.",
                color = TeddTokens.InkMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        } else {
            Text(
                text = "$rate %",
                style = TeddType.Numeral.copy(fontSize = 44.sp, lineHeight = 48.sp),
                color = TeddTokens.Ink,
                modifier = Modifier.padding(top = 6.dp),
            )
            Text(
                text = buildString {
                    append("$needCount besoin${if (needCount > 1) "s" else ""} réel${if (needCount > 1) "s" else ""}")
                    append(" · $masteredCount désir${if (masteredCount > 1) "s" else ""} maîtrisé${if (masteredCount > 1) "s" else ""}")
                    if (impulseCount > 0) append(" · $impulseCount sans réponse")
                },
                color = TeddTokens.InkMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 6.dp),
            )
        }
    }
}
